import { useCallback, useContext, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { useAppDispatch, shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import uuidV4 from 'lib/string/uuidV4Utils'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import { transformUserCartItemsToCheckoutItems } from 'lib/checkout/userCart'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { getOffersById } from 'api/offer'
import { reportLog } from 'services/userMonitoring'
import { fetchCharterBundleFlightItems, getCharterBundleStay, resolveCharterBundle, type CharterBundleHydrationResult, type CharterFlightBundleSelection } from 'tripPlanner/utils/charterBundleHydration'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import { useShouldWaiveLuxPlusJoiningFee } from 'hooks/UserCart/useCartItems'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { isExperienceItem, isFlightItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import { getMultiCityGroupId, getMultiCityLegOrder } from '../utils/multiCityFlightHelpers'
import type { CartPanelFlightItem, CartPanelItem, CheckoutReadyItem } from './cartPanelItemTypes'
import {
  getCartPanelExperienceTimesKey,
  isCartPanelItemCheckoutReady,
  prepareCartPanelExperienceCheckoutItem,
} from '../utils/cartPanelUtils'
import { refreshTripChatBedbankItemSessions } from '../utils/tripChatCheckoutBedbankUtils'
import { createAccommodationV2SessionsForCartItems } from 'lib/checkout/createAccommodationV2SessionsForCartItems'
import { getCheckoutMultiCityGroups, hasLiveFlightSearch, refreshTripChatMultiCityFlights } from '../utils/tripChatCheckoutFlightUtils'
import { isActiveFlightAvailabilityBlocked } from '../utils/flightCartPanelUtils'
import { getTripPlannerCandidateCartItem } from '../utils/tripPlannerCartItemsUtils'
import { FlightViewTypes } from 'constants/flight'

interface UseTripChatCheckoutActionsArgs {
  chatId: number | undefined
  multiCartId: string | undefined
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

interface UseTripChatCheckoutActionsReturn {
  checkoutCartItems: (items: Array<CartPanelItem>) => Promise<void>
  prepareCheckoutCartItems: (items: Array<CartPanelItem>) => void
  isCheckingOut: boolean
}

interface PreparedFlights {
  inputKey: string
  cartItems: Map<string, App.MultiCartItem>
  preparedAt: number
}

interface PendingPreparedFlights {
  inputKey: string
  promise: Promise<PreparedFlights>
}

type FreshCheckoutPreparation =
  | { status: 'ready'; items: Array<CheckoutReadyItem> }
  | { status: 'cart_refetch_failed' }
  | { status: 'flight_refresh_failed'; error: unknown }

const PREPARED_FLIGHTS_MAX_AGE_MS = 2 * 60 * 1000

function isCheckoutReadyItem(item: CartPanelItem): item is CheckoutReadyItem {
  return isCartPanelItemCheckoutReady(item) && !!item.cartItem
}

function expandCartPanelItems(items: Array<CartPanelItem>): Array<CartPanelItem> {
  return items.flatMap(item =>
    item.productType === 'flight' && item.multiCityLegs ? item.multiCityLegs : [item],
  )
}

function getCheckoutReadyItems(items: Array<CartPanelItem>): Array<CheckoutReadyItem> {
  return expandCartPanelItems(items.filter(isCartPanelItemCheckoutReady)).filter(isCheckoutReadyItem)
}

function withFreshCartItemSnapshots(
  item: CartPanelItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelItem {
  const freshItem = withFreshCartItemSnapshot(item, cartItems)
  if (freshItem.productType !== 'flight' || !freshItem.multiCityLegs) return freshItem

  return {
    ...freshItem,
    multiCityLegs: freshItem.multiCityLegs.map(leg => withFreshFlightCartItemSnapshot(leg, cartItems)),
  }
}

function isPreparedFlightsUsable(
  prepared: PreparedFlights | undefined,
  inputKey: string,
): prepared is PreparedFlights {
  return !!prepared &&
    prepared.inputKey === inputKey &&
    Date.now() - prepared.preparedAt < PREPARED_FLIGHTS_MAX_AGE_MS &&
    [...prepared.cartItems.values()].every(({ item }) => hasLiveFlightSearch(item))
}

function withFreshFlightCartItemSnapshot(
  item: CartPanelFlightItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelFlightItem {
  if (!item.cartItemId) return item
  const freshCartItem = cartItems.get(item.cartItemId)
  const flightCartItem = freshCartItem && isMultiCartFlightItem(freshCartItem) ? freshCartItem : undefined
  // Availability comes from the cart row, so it is re-derived from the fresh one.
  const isAvailabilityBlocked = isActiveFlightAvailabilityBlocked(flightCartItem)
  return {
    ...item,
    cartItem: flightCartItem,
    flightCartItem,
    isAvailabilityBlocked,
    pricing: { ...item.pricing, isAvailable: !isAvailabilityBlocked },
  }
}

function withFreshCartItemSnapshot(
  item: CartPanelItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelItem {
  if (item.productType === 'flight') return withFreshFlightCartItemSnapshot(item, cartItems)
  return { ...item, cartItem: getTripPlannerCandidateCartItem(cartItems, item) }
}

function toCheckoutItem(
  item: CheckoutReadyItem,
  currency: string,
  experiences: App.State['experience']['experiences'],
  experienceTimes: App.State['experience']['experienceTimes'],
): App.Checkout.AnyItem {
  const checkoutItem = {
    ...item.cartItem.item,
    itemId: item.cartItem.item.itemId ?? item.cartItem.id,
    transactionKey: item.cartItem.item.transactionKey ?? uuidV4(),
  }
  if (item.productType !== 'experience' || !isExperienceItem(checkoutItem)) return checkoutItem

  const key = getCartPanelExperienceTimesKey(checkoutItem, currency)
  return prepareCartPanelExperienceCheckoutItem(
    checkoutItem,
    experiences[checkoutItem.experienceId],
    experienceTimes[checkoutItem.experienceId]?.[key]?.slots,
  )
}

function combineMultiCityGroup(legCheckoutItems: Array<App.Checkout.AnyItem>): Array<App.Checkout.AnyItem> {
  const flightItems = legCheckoutItems.filter(isFlightItem)
  const flights = flightItems.flatMap(checkoutItem => checkoutItem.flights)
  const [firstCheckoutItem] = flightItems
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  // A leg may carry several segments (a connection); every leg must be a flight
  // item, but the combined itinerary keeps all segments in order.
  if (!firstCheckoutItem || !firstFlight || !lastFlight || flightItems.length !== legCheckoutItems.length) {
    return legCheckoutItems
  }

  return [{
    ...firstCheckoutItem,
    fareType: 'multiCity',
    viewType: FlightViewTypes.MULTI_CITY,
    originAirportCode: firstFlight.departingAirportCode,
    destinationAirportCode: lastFlight.arrivalAirportCode,
    hasFinalFare: false,
    fareFetchError: undefined,
    // Re-derived from every leg's flights; leg one's standalone total would
    // undercharge the group until the final fare is fetched.
    totalFare: undefined,
    flights,
  }]
}

function combineMultiCityCheckoutItems(
  activeItems: Array<CheckoutReadyItem>,
  checkoutItems: Array<App.Checkout.AnyItem>,
): Array<App.Checkout.AnyItem> {
  const groupByLegKey = new Map<string, Array<CheckoutReadyItem>>()
  getCheckoutMultiCityGroups(activeItems).forEach(group => group.forEach(leg => groupByLegKey.set(leg.key, group)))
  if (groupByLegKey.size === 0) return checkoutItems

  const checkoutItemByKey = new Map(activeItems.map((item, index) => [item.key, checkoutItems[index]]))
  const combinedGroups = new Set<Array<CheckoutReadyItem>>()
  return activeItems.flatMap((item, index) => {
    const group = groupByLegKey.get(item.key)
    if (!group) return [checkoutItems[index]]
    if (combinedGroups.has(group)) return []
    combinedGroups.add(group)
    const legCheckoutItems = [...group]
      .sort((a, b) => getMultiCityLegOrder(a) - getMultiCityLegOrder(b))
      .flatMap(leg => checkoutItemByKey.get(leg.key) ?? [])
    return combineMultiCityGroup(legCheckoutItems)
  })
}

export function useTripChatCheckoutActions({
  chatId,
  multiCartId,
  refetchCartItems,
}: UseTripChatCheckoutActionsArgs): UseTripChatCheckoutActionsReturn {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && currentRegionCode === 'AU'
  const shouldWaive = useShouldWaiveLuxPlusJoiningFee()
  const checkoutInFlightRef = useRef(false)
  const preparedFlightsRef = useRef<PreparedFlights | undefined>(undefined)
  const pendingPreparedFlightsRef = useRef<PendingPreparedFlights | undefined>(undefined)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const { subscriptionOffers, experiences, experienceTimes, email } = useAppSelector(state => ({
    subscriptionOffers: state.luxPlus.products.subscription.offers,
    experiences: state.experience.experiences,
    experienceTimes: state.experience.experienceTimes,
    email: state.auth?.account?.email,
  }), shallowEqual)
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)
  const enableSabreContent = useFeatureFlag(OptimizelyFeatureFlags.flightsEnableSabreContent)

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })

  const flightRefreshErrorMessage = intl.formatMessage({
    id: 'J1dfTHgb7DOLNSkugHPz',
    defaultMessage: 'This flight is no longer available. Please search for new flights.',
    description: 'Error shown when a multi-city flight group cannot be re-priced at checkout',
  })

  const charterBundleErrorMessage = intl.formatMessage({
    id: 'oS-wuNEBq6t8u5nGT-2z',
    defaultMessage: 'The included flight isn’t available for your selected dates. Please adjust your stay and try again.',
    description: 'Error shown when the bundled charter flight cannot be added at checkout',
  })

  const emptyCheckoutMessage = intl.formatMessage({
    id: 'NrlaeuBIm_kkUCkneM7W',
    defaultMessage: 'No items are ready to book. Please review your cart and try again.',
    description: 'Warning shown when refreshing Trip Chat checkout finds no remaining items ready to book',
  })

  const prepareFlights = useCallback(async(activeItems: Array<CheckoutReadyItem>): Promise<Array<CheckoutReadyItem>> => {
    const flightItems = getCheckoutMultiCityGroups(activeItems).flat()
    if (flightItems.length === 0) return activeItems

    // Cache only flight searches. Cart selection and readiness are rebuilt on
    // every checkout click, even while a matching flight search is in flight.
    const deps = {
      currentCurrency: currency,
      currentRegionCode,
      email,
      enableSabreContent,
      requestLuxPlusPricing: canRequestLuxPlusFlightPricing,
    }
    const inputKey = JSON.stringify({
      chatId,
      multiCartId,
      ...deps,
      flights: flightItems.map(item => ({
        key: item.key,
        cartItemId: item.cartItemId,
        groupId: getMultiCityGroupId(item),
        legOrder: getMultiCityLegOrder(item),
        cartItem: item.cartItem,
      })),
    })
    let prepared = preparedFlightsRef.current
    if (!isPreparedFlightsUsable(prepared, inputKey)) {
      let pending = pendingPreparedFlightsRef.current
      if (pending?.inputKey !== inputKey) {
        const promise = refreshTripChatMultiCityFlights(flightItems, deps).then(items => ({
          inputKey,
          cartItems: new Map(items.map(item => [item.cartItem.id, item.cartItem])),
          preparedAt: Date.now(),
        }))
        pending = { inputKey, promise }
        pendingPreparedFlightsRef.current = pending
      }
      try {
        prepared = await pending.promise
        if (pendingPreparedFlightsRef.current === pending) preparedFlightsRef.current = prepared
      } finally {
        if (pendingPreparedFlightsRef.current === pending) pendingPreparedFlightsRef.current = undefined
      }
    }
    const preparedCartItems = prepared.cartItems
    return activeItems.map(item => {
      const cartItem = preparedCartItems.get(item.cartItem.id)
      if (item.productType !== 'flight' || !cartItem || !isMultiCartFlightItem(cartItem)) return item
      return { ...item, cartItem, flightCartItem: cartItem }
    })
  }, [chatId, multiCartId, currency, currentRegionCode, email, enableSabreContent, canRequestLuxPlusFlightPricing])

  const prepareFreshCheckoutItems = useCallback(async(items: Array<CartPanelItem>): Promise<FreshCheckoutPreparation> => {
    let freshCartItems: Map<string, App.MultiCartItem>
    try {
      freshCartItems = await refetchCartItems()
    } catch {
      return { status: 'cart_refetch_failed' }
    }

    const activeItems = getCheckoutReadyItems(items.map(item => withFreshCartItemSnapshots(item, freshCartItems)))
    try {
      return { status: 'ready', items: await prepareFlights(activeItems) }
    } catch(error) {
      return { status: 'flight_refresh_failed', error }
    }
  }, [refetchCartItems, prepareFlights])

  const prepareCheckoutCartItems = useCallback((items: Array<CartPanelItem>) => {
    if (!chatId || !multiCartId) return
    const activeItems = getCheckoutReadyItems(items)
    prepareFlights(activeItems).catch(() => undefined)
  }, [chatId, multiCartId, prepareFlights])

  const checkoutCartItems = useCallback(async(items: Array<CartPanelItem>) => {
    if (!chatId || !multiCartId || checkoutInFlightRef.current) return
    checkoutInFlightRef.current = true
    setIsCheckingOut(true)

    try {
      const preparation = await prepareFreshCheckoutItems(items)
      if (preparation.status === 'cart_refetch_failed') {
        showSnackbar(defaultErrorMessage, 'critical')
        return
      }
      if (preparation.status === 'flight_refresh_failed') {
        reportLog('Trip chat checkout flight refresh failed', { error: preparation.error, chatId, multiCartId }, 'warn')
        showSnackbar(flightRefreshErrorMessage, 'critical')
        return
      }
      const activeItems = preparation.items
      if (activeItems.length === 0) {
        showSnackbar(emptyCheckoutMessage, 'warning')
        return
      }

      getCartMachineActor()?.send({ type: 'set active cart', cartId: multiCartId })

      const checkoutSource: App.CheckoutSource = {
        type: 'trip_planner',
        multiCartId,
        externalReferenceId: String(chatId),
      }
      const perActiveItemCheckoutItems = activeItems.map(item => toCheckoutItem(item, currency, experiences, experienceTimes))
      const checkoutItems = combineMultiCityCheckoutItems(activeItems, perActiveItemCheckoutItems)

      // Charter flight bundles (Maldives MEL↔MLE direct): detect each charter
      // bundle from the live offer (shared helper; requireFlightsEnabled is false
      // at checkout because getOffersById doesn't reliably populate it) and derive
      // its charter (+ feeder) flight legs, then inject them so checkout opens with
      // the bundled flight. Batch the offer fetch so we don't fire one request per
      // hotel on the checkout-click path.
      const accommodationOfferIds = [...new Set(
        activeItems.flatMap(item =>
          item.productType === 'accommodation' && item.offerId ? [item.offerId] : [],
        ),
      )]
      const offersById = new Map<string, App.AnyOffer>()
      if (accommodationOfferIds.length > 0) {
        try {
          const offers = await getOffersById(accommodationOfferIds, currentRegionCode)
          for (const offer of offers) {
            if ('id' in offer) offersById.set(offer.id, offer)
          }
        } catch { /* per-item missing-offer guard below decides whether to block */ }
      }

      const charterBundleItems: Array<{
        offer: App.HotelOffer | App.HotelOfferSummary
        charterFlightBundle: CharterFlightBundleSelection
        occupancies: Array<App.Occupants>
        checkIn: string
        checkOut: string
        checkoutItem: App.Checkout.InstantBookingLEHotelItem
      }> = []
      for (let i = 0; i < activeItems.length; i++) {
        const item = activeItems[i]
        if (item.productType !== 'accommodation' || !item.offerId) continue
        const offer = offersById.get(item.offerId)
        const checkoutItem = perActiveItemCheckoutItems[i]
        const instantBookingItem = isInstantBookingLEHotelItem(checkoutItem) ? checkoutItem : undefined
        // Missing offer: getOffersById dropped this one (it can return a subset),
        // so we can't inspect it to rule out a charter bundle. Fail closed only for
        // items that could actually carry a charter flight - a persisted selection,
        // or an instant-booking LE hotel (the only item type charter bundles ride
        // on). Unrelated accommodation (bedbank/SPP/villa, which getOffersById may
        // not even return) checks out normally instead of blocking the whole cart.
        if (!offer) {
          if (isMaldivesCharterEnabled && (item.charterFlightBundle || instantBookingItem)) {
            showSnackbar(charterBundleErrorMessage, 'critical')
            return
          }
          continue
        }
        const resolved = resolveCharterBundle({
          offer,
          isMaldivesCharterEnabled,
          persistedBundle: item.charterFlightBundle,
          occupancies: getCharterBundleStay(checkoutItem, {
            checkIn: item.checkIn,
            checkOut: item.checkOut,
            occupancies: item.occupancies,
          }).occupancies,
          requireFlightsEnabled: false,
        })
        if (!resolved) {
          if (item.charterFlightBundle) {
            showSnackbar(charterBundleErrorMessage, 'critical')
            return
          }
          continue
        }
        // The charter legs must ride on an instant-booking LE hotel item; if a
        // detected bundle isn't one we can't build its flight, so block checkout
        // rather than silently opening it flight-less.
        if (!instantBookingItem) {
          showSnackbar(charterBundleErrorMessage, 'critical')
          return
        }
        charterBundleItems.push({
          offer: resolved.offer,
          charterFlightBundle: resolved.charterFlightBundle,
          occupancies: resolved.occupancies,
          checkIn: instantBookingItem.checkIn,
          checkOut: instantBookingItem.checkOut,
          checkoutItem: instantBookingItem,
        })
      }

      // Hydrate every charter bundle's flight legs in parallel (one search per hotel).
      let charterResults: Array<CharterBundleHydrationResult>
      try {
        charterResults = await Promise.all(charterBundleItems.map(({ offer, charterFlightBundle, occupancies, checkIn, checkOut, checkoutItem }) =>
          fetchCharterBundleFlightItems({
            offer,
            charterFlightBundle,
            accommodationItems: [checkoutItem],
            checkIn,
            checkOut,
            occupancies,
            region: currentRegionCode,
            currency,
            email,
          }),
        ))
      } catch(error) {
        reportLog('Trip chat charter bundle hydration threw', {
          error,
          chatId,
          multiCartId,
          offerIds: charterBundleItems.map(({ offer }) => offer.id),
        }, 'error')
        showSnackbar(charterBundleErrorMessage, 'critical')
        return
      }

      const charterFlightItems: Array<App.Checkout.FlightItem> = []
      let hydrationFailed = false
      for (let i = 0; i < charterResults.length; i++) {
        const result = charterResults[i]
        if (!result.ok) {
          hydrationFailed = true
          const { offer, charterFlightBundle, checkIn, checkOut } = charterBundleItems[i]
          reportLog('Trip chat charter bundle hydration failed', {
            reason: result.reason,
            offerId: offer.id,
            charterBundleRoute: charterFlightBundle.charterBundleRoute,
            originAirportCode: charterFlightBundle.originAirportCode,
            fareClass: charterFlightBundle.fareClass,
            checkIn,
            checkOut,
            chatId,
            multiCartId,
          }, 'error')
          continue
        }
        charterFlightItems.push(...result.flightItems)
      }
      if (hydrationFailed) {
        showSnackbar(charterBundleErrorMessage, 'critical')
        return
      }

      let transformed = transformUserCartItemsToCheckoutItems(checkoutItems, subscriptionOffers, shouldWaive)
      try {
        transformed = await refreshTripChatBedbankItemSessions(transformed, dispatch)
        transformed = await createAccommodationV2SessionsForCartItems(transformed, dispatch)
      } catch {
        showSnackbar(defaultErrorMessage, 'critical')
        return
      }

      const allItems = [...transformed, ...charterFlightItems]
      dispatch(initialiseCheckout(allItems.map(item => ({ ...item, transactionKey: uuidV4() })), { checkoutSource }, true))
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    } finally {
      checkoutInFlightRef.current = false
      setIsCheckingOut(false)
    }
  }, [chatId, multiCartId, prepareFreshCheckoutItems, emptyCheckoutMessage, flightRefreshErrorMessage, charterBundleErrorMessage, defaultErrorMessage, isMaldivesCharterEnabled, experiences, experienceTimes, subscriptionOffers, shouldWaive, dispatch, currentRegionCode, currency, email])

  return { checkoutCartItems, prepareCheckoutCartItems, isCheckingOut }
}
